import {
  buildProjectModelDesignTreeProjection,
  createAssemblyTransform,
  createInternalPuppyAssemblyDirectChildReferenceLocator,
  createInternalPuppyAssemblyExpandTransactionState,
  createProjectModelLoadPlan,
  executeProjectModelLoad,
  insertAssemblyChild,
  listAssemblyDocumentDirectChildNodes,
  mountLoadedSubtreeOccurrenceIdentities,
  parseRepositoryPuppyNodeWhere,
  ProjectModelRuntimeStateResolver,
  resolveMissingFirstLevelNodeAssemblyNodeId,
  resolveProjectModelNodeAssemblyNodeId,
  recordInternalPuppyAssemblyInsertedNode,
  resolveReusableInternalPuppyAssemblyInsertedNodeKey,
  type AssemblyDocument,
  type AssemblyDocumentDirectChildReference,
  type AssemblyDocumentNode,
  type AssemblyTransform,
  type ModelAssetNode,
  type ModelAssetReader,
  type ProjectModelLoadResult,
  type ProjectModelSceneBinding,
  type PuppyAssemblyProjectModelCoverageContext,
  type ResolvedPlacementTransform,
  type InternalPuppyAssemblyExpandTransactionState,
} from '@puppycad/cad-core'
import type { CadNode } from '@puppycad/proto'
import { CadFileKind } from '@puppycad/proto'
import type {
  MergePreparedSwitchFirstLevelNodeVersionResultInput,
} from '../../../types/project-model-loading/project-model-loading-service'
export type {
  MergePreparedSwitchFirstLevelNodeVersionResultInput,
} from '../../../types/project-model-loading/project-model-loading-service'
import { CadArchiveFileSnapshotMapper } from './CadArchiveFileSnapshotMapper'
import { createInsertedComponentNodeSnapshotResult } from './inserted-component-node-coordinator'
import { resolveAssemblyOfflinePackageFile } from './assembly-offline-package-resolver'
import { mapCadNodeToModelAssetNode } from './open-project-asset-tree-mapper'
import { checkPuppyAssemblyDirectChildren } from './project-model-puppy-assembly-direct-child-checker'
import type {
  AppendMissingFirstLevelNodeVersionInput,
  CadNodeLoadSnapshot,
  LoadFirstLevelNodeVersionSubtreeInput,
  LoadOpenProjectInput,
  LoadProjectModelFromCadNodeInput,
  LoadProjectModelFromCadNodeResult,
  LoadRootSnapshotInput,
  MaterializeWorkingAssemblyFirstLevelNodesInput,
  NodeReloadApiAdapter,
  ProjectModelFileByteCacheAdapter,
  SwitchFirstLevelNodeVersionInput,
} from './types/project-model-loading-types'

const projectModelRuntimeStateResolver = new ProjectModelRuntimeStateResolver()
const IDENTITY_TRANSFORM: AssemblyTransform = [
  1, 0, 0, 0,
  0, 1, 0, 0,
  0, 0, 1, 0,
  0, 0, 0, 1,
]

/**
 * 创建仅服务当前加载事务的空文件缓存。
 *
 * 设计说明：旧 service 入口没有缓存参数，但一旦接入统一 preparer，就必须满足其缓存契约。
 * 这里提供只读空缓存，保证旧调用行为不引入跨事务共享状态，也不会误判某个文件已经可读。
 *
 * @returns 不命中任何文件的缓存适配器。
 */
function createEmptyProjectModelFileByteCacheAdapter(): ProjectModelFileByteCacheAdapter {
  return {
    has: () => false,
    read: () => null,
    keys: () => [],
  }
}

/**
 * 归一化局部版本切换入口拿到的后端 CadNode 快照。
 *
 * 设计说明：GetCadNodeTree 会返回目标节点的完整子树；当目标是零件且 files 中已经有完整 `.puppy`
 * 包时，SolidWorks 语义下该包才是零件实体与拓扑的权威来源。后端快照里残留的 children 只能表示历史
 * 打包或旧结构展开结果，若继续交给项目模型 mapper，会把零件误投影成“零件嵌套零件/feature”。
 * 这里仅服务于子节点版本切换，不改变普通 OpenProject/root 加载入口。
 *
 * @param node GetCadNodeTree 返回的目标节点快照。
 * @returns 可安全交给局部版本切换 mapper 的节点快照。
 */
function normalizeNodeVersionSwitchCadNodeSnapshot(node: CadNode): CadNode {
  if (node.type !== CadFileKind.PART || node.children.length === 0) {
    return node
  }

  const packageFile = resolveAssemblyOfflinePackageFile(node)
  if (!packageFile) {
    return node
  }

  return {
    ...node,
    children: [],
  }
}

/**
 * 加载插入零部件的输入。
 */
export interface LoadInsertedComponentInput {
  /**
   * 当前已加载的模型资产根节点。
   */
  readonly root: ModelAssetNode
  /**
   * 插入零部件所属的真实父节点 key。
   */
  readonly parentNodeKey: string
  /**
   * 后端服务根地址，用于生成新增节点版本文件下载 URL。
   */
  readonly backendBaseUrl: string
  /**
   * 插入接口返回的新建 CAD 节点。
   */
  readonly createdNode: CadNode
  /**
   * 用户确认放置后解析出的局部矩阵。
   */
  readonly resolvedPlacementTransform: ResolvedPlacementTransform
  /**
   * cad-core 模型资产读取器。
   */
  readonly reader: ModelAssetReader
  /**
   * 当前已有场景对象绑定；插入节点尚未完成几何解析时，保留未受影响节点的 objectId 链路。
   */
  readonly sceneBindings?: readonly ProjectModelSceneBinding[]
  /**
   * 当前已有装配恢复文档索引；局部插入只读取新增子树，必须显式保留未受影响节点的文档。
   */
  readonly assemblyDocumentsByNodeKey?: ProjectModelLoadResult['assemblyDocumentsByNodeKey']
  /**
   * 可选取消信号，用于中断新增节点文件下载。
   */
  readonly signal?: AbortSignal
}

/**
 * 加载插入零部件后的项目模型结果。
 */
export interface LoadInsertedComponentResult extends ProjectModelLoadResult {
  /**
   * 本次插入产生的新节点 key。
   */
  readonly insertedNodeKey: string
}

/**
 * 子树合并后的项目模型结果。
 */
export interface ProjectModelSubtreeMergeResult extends ProjectModelLoadResult {
  /**
   * 本次结构插入产生的节点 key；仅 append missing 场景需要返回。
   */
  readonly insertedNodeKey?: string
}

/**
 * 创建一级节点版本切换加载单元的窄输入。
 */
export interface CreateSwitchFirstLevelNodeVersionLoadUnitInput {
  /**
   * 当前项目模型加载结果。
   */
  readonly currentResult: ProjectModelLoadResult
  /**
   * 需要替换的一级子节点 key。
   */
  readonly targetNodeKey: string
  /**
   * 父装配工作文档中的直接子节点 key；归档运行态的 canonical key 与文档 key 不同时必须显式提供。
   */
  readonly assemblyDocumentNodeKey?: string
  /**
   * 替换根节点应继承的父装配放置矩阵。
   */
  readonly replacementRootTransform?: readonly number[]
  /**
   * 按节点 key 覆盖加载事务使用的装配文档。
   */
  readonly assemblyDocumentOverridesByNodeKey?: ReadonlyMap<string, AssemblyDocument>
  /** 新版本子节点及响应级总归档的完整快照。 */
  readonly nextChildSnapshot: CadNodeLoadSnapshot
  /**
   * 后端服务根地址，用于生成版本文件下载地址。
   */
  readonly backendBaseUrl: string
}

/**
 * 创建缺失一级节点版本补加载单元的窄输入。
 */
export interface CreateAppendMissingFirstLevelNodeVersionLoadUnitInput {
  /**
   * 当前项目模型加载结果。
   */
  readonly currentResult: ProjectModelLoadResult
  /**
   * working 装配文档中的稳定一级子节点 key。
   */
  readonly targetNodeKey: string
  /**
   * 父装配工作文档中的直接子节点 key；归档运行态的 canonical key 与文档 key 不同时必须显式提供。
   */
  readonly assemblyDocumentNodeKey?: string
  /**
   * 补入节点在父装配下应使用的放置矩阵。
   */
  readonly replacementRootTransform?: readonly number[]
  /**
   * 按节点 key 覆盖加载事务使用的装配文档。
   */
  readonly assemblyDocumentOverridesByNodeKey?: ReadonlyMap<string, AssemblyDocument>
  /** 目标采纳版本节点及响应级总归档的完整快照。 */
  readonly nextChildSnapshot: CadNodeLoadSnapshot
  /**
   * 后端服务根地址，用于生成版本文件下载地址。
   */
  readonly backendBaseUrl: string
}

/**
 * 创建一级节点版本独立子树加载单元的窄输入。
 */
export interface CreateFirstLevelNodeVersionSubtreeLoadUnitInput {
  /**
   * 父装配节点 key。
   */
  readonly parentNodeKey: string
  /**
   * 当前装配关系中稳定的目标子节点 key。
   */
  readonly targetNodeKey: string
  /**
   * 目标子节点在父装配 children 中的顺序。
   */
  readonly siblingIndex: number
  /**
   * 父装配工作草稿中记录的目标节点放置矩阵。
   */
  readonly replacementRootTransform: readonly number[]
  /** 后端返回的新版本节点及响应级总归档的完整快照。 */
  readonly nextChildSnapshot: CadNodeLoadSnapshot
  /**
   * 后端服务根地址，用于生成版本文件下载地址。
   */
  readonly backendBaseUrl: string
  /**
   * 外层传入的装配文档覆盖表。
   */
  readonly assemblyDocumentOverridesByNodeKey?: ReadonlyMap<string, AssemblyDocument>
}

/**
 * 合并 prepared 缺失一级节点补加载结果的窄输入。
 */
export interface MergePreparedAppendMissingFirstLevelNodeVersionResultInput {
  /**
   * 当前已经加载完成的完整项目模型结果。
   */
  readonly currentResult: ProjectModelLoadResult
  /**
   * 需要追加的一级子节点 key。
   */
  readonly targetNodeKey: string
  /**
   * 已经通过统一 preparer 完成加载的待插入子树结果。
   */
  readonly preparedSubtreeResult: ProjectModelLoadResult
  /**
   * 调用方提供的高优先级装配文档覆盖。
   */
  readonly assemblyDocumentOverridesByNodeKey?: ReadonlyMap<string, AssemblyDocument>
  /**
   * 调用方补充的装配文档索引，通常来自当前编辑态 working 文档。
   */
  readonly assemblyDocumentsByNodeKey?: ReadonlyMap<string, AssemblyDocument>
}

/**
 * 合并 prepared 一级节点独立子树结果的窄输入。
 */
export interface MergePreparedFirstLevelNodeVersionSubtreeResultInput {
  /**
   * 当前装配关系中稳定的目标子节点 key。
   */
  readonly targetNodeKey: string
  /**
   * 已经通过统一 preparer 完成加载的目标子树结果。
   */
  readonly preparedSubtreeResult: ProjectModelLoadResult
  /**
   * 调用方提供的高优先级装配文档覆盖。
   */
  readonly assemblyDocumentOverridesByNodeKey?: ReadonlyMap<string, AssemblyDocument>
  /**
   * 调用方补充的装配文档索引，通常来自当前编辑态 working 文档。
   */
  readonly assemblyDocumentsByNodeKey?: ReadonlyMap<string, AssemblyDocument>
}

/**
 * 替换子树时的父节点定位结果。
 */
interface ProjectModelSubtreeLocation {
  /**
   * 当前模型资产树中的目标节点。
   */
  readonly node: ModelAssetNode
  /**
   * 目标节点的父节点 key。
   */
  readonly parentNodeKey: string
  /**
   * 目标节点深度。
   */
  readonly depth: number
  /**
   * 目标节点在父节点 children 中的序号。
   */
  readonly siblingIndex: number
}

/**
 * 插入零部件入口输入。
 */
export interface InsertComponentInput extends Omit<LoadInsertedComponentInput, 'root' | 'sceneBindings'> {
  /**
   * 当前项目模型加载结果。
   */
  readonly currentResult: ProjectModelLoadResult
  /**
   * 后端创建提交信息；首版只作为调用方刷新分支列表的上下文，不在模型加载服务内持久化。
   */
  readonly createdCommit?: unknown
}

/**
 * 加载插入零部件入口结果。
 */
export interface InsertComponentResult extends ProjectModelLoadResult {
  /**
   * 本次插入产生的新节点 key。
   */
  readonly insertedNodeKey: string
}

/**
 * 后端树扩展装配直系子节点快照的输入。
 */
export interface CreateRootPuppyAssemblyMissingFirstLevelNodeSnapshotInput {
  /**
   * 当前已经读取加载单元文件与 `_assembly.json` 的加载结果。
   */
  readonly currentResult: ProjectModelLoadResult
  /**
   * 后端服务根地址，用于把补齐节点的版本文件映射为下载 URL。
   */
  readonly backendBaseUrl: string
  /**
   * 获取采纳版本完整 CadNode 子树的适配器。
   */
  readonly nodeReloadAdapter: NodeReloadApiAdapter
  /** 已从当前实际 `.puppy` 解析出的 project-model 根直系 coverage 上下文。 */
  readonly coverageContext?: PuppyAssemblyProjectModelCoverageContext | null
  /** complete GLB 选中根的业务一级 occurrence coverage。 */
  readonly completeGlbCoverage?: PuppyCADWeb.CompleteGlbDirectChildCoverage
}

/**
 * 后端 expand 编排层调用 direct child 快照的内部输入。
 *
 * @internal 该类型不从 project-model-loading barrel 导出。
 */
export interface InternalCreateRootPuppyAssemblyMissingFirstLevelNodeSnapshotInput
  extends CreateRootPuppyAssemblyMissingFirstLevelNodeSnapshotInput {
  /** 同一次 expand 中跨 pass 传递的隔离事务状态。 */
  readonly internalTransactionState: InternalPuppyAssemblyExpandTransactionState
}

/**
 * 后端树扩展装配直系子节点快照的结果。
 */
export interface RootPuppyAssemblyMissingFirstLevelNodeSnapshotResult {
  /**
   * 当前 root 快照；发生扩展时会包含追加后的一级 children。
   */
  readonly root: ModelAssetNode
  /**
   * 本次根据采纳版本成功补入的一级节点 key；没有扩展时为空。
   */
  readonly insertedNodeKeys: readonly string[]
  /**
   * 因尚无采纳版本而跳过的一级节点 key。
   */
  readonly skippedUnversionedNodeKeys: readonly string[]
  /**
   * 因版本引用字段损坏而跳过的一级节点 key。
   */
  readonly invalidNodeKeys: readonly string[]
}

/**
 * 后端 expand 编排层调用 direct child 快照的内部输出。
 *
 * @internal 该类型不从 project-model-loading barrel 导出。
 */
export interface InternalRootPuppyAssemblyMissingFirstLevelNodeSnapshotOutput {
  /** 不包含内部可变映射的公共快照结果。 */
  readonly result: RootPuppyAssemblyMissingFirstLevelNodeSnapshotResult
  /** 供下一轮 expand 使用的全新事务状态快照。 */
  readonly internalTransactionState: InternalPuppyAssemblyExpandTransactionState
}

/**
 * 创建空子节点 working 文档的输入。
 */
export interface CreateEmptyChildWorkingAssemblyDocumentInput {
  /**
   * 当前项目模型加载结果；只用于派生稳定 nodeKey，不会写回设计树。
   */
  readonly currentResult: ProjectModelLoadResult
  /**
   * 父装配当前的工作草稿。
   */
  readonly workingAssemblyDocument: AssemblyDocument
  /**
   * 后端服务根地址，用于复用统一的 CadNode 到模型节点 key 映射链路。
   */
  readonly backendBaseUrl: string
  /**
   * CreateSubAssembly 返回的新建 CAD 节点。
   */
  readonly createdNode: CadNode
  /**
   * 后端未返回实例名时使用的用户输入名称。
   */
  readonly fallbackDisplayName: string
  /**
   * 新节点在父装配下的放置矩阵。
   */
  readonly resolvedPlacementTransform: ResolvedPlacementTransform
}

/**
 * 创建空子节点 working 文档的结果。
 */
export interface CreateEmptyChildWorkingAssemblyDocumentResult {
  /**
   * 新增节点的稳定结构 key。
   */
  readonly insertedNodeKey: string
  /**
   * 写入空子节点后的 working 装配文档。
   */
  readonly workingAssemblyDocument: AssemblyDocument
}

/**
 * 将普通矩阵数组收口为装配文档矩阵。
 *
 * 设计说明：装配文档是后续保存与补加载的权威结构来源，写入前必须阻止长度错误或 NaN 进入。
 *
 * @param matrix 待写入的 4x4 展平矩阵。
 * @param context 错误定位上下文。
 * @returns 可写入装配文档的矩阵副本。
 */
function toAssemblyTransform(matrix: readonly number[], context: string): AssemblyTransform {
  return createAssemblyTransform(matrix, context)
}

/**
 * 创建可写入装配文档的子节点摘要。
 *
 * @param input CadNode 与结构 key 上下文。
 * @returns cad-core 装配文档子节点。
 */
function createAssemblyDocumentNodeFromCadNode(input: {
  readonly node: CadNode
  readonly nodeKey: string
  readonly transform: AssemblyTransform
  readonly fallbackDisplayName: string
  readonly adoptCreatedVersion: boolean
}): AssemblyDocumentNode {
  const instanceId = input.node.instanceId.trim()
  const instanceName = input.node.instanceName.trim() || input.fallbackDisplayName.trim()

  return {
    nodeKey: input.nodeKey,
    nodeType: input.node.type === CadFileKind.PART ? 'part' : 'assembly',
    repositoryId: input.node.repositoryId.toString(),
    ...(input.adoptCreatedVersion && input.node.branchId > 0n ? { branchId: input.node.branchId.toString() } : {}),
    commitId: input.adoptCreatedVersion ? input.node.commitId.trim() : '',
    transform: input.transform,
    ...(instanceId ? { instanceId } : {}),
    ...(instanceName ? { instanceName } : {}),
  }
}

/**
 * 合并当前加载结果与调用方覆盖的装配文档。
 *
 * @param current 当前模型结果已有的装配文档索引。
 * @param overrides 调用方指定的更高优先级文档。
 * @returns 可传入局部加载事务的文档索引。
 */
function mergeAssemblyDocumentOverrides(
  current: ReadonlyMap<string, AssemblyDocument>,
  overrides?: ReadonlyMap<string, AssemblyDocument>,
): ReadonlyMap<string, AssemblyDocument> {
  const merged = new Map(current)

  overrides?.forEach((document, nodeKey) => {
    merged.set(nodeKey, document)
  })

  return merged
}

/**
 * 从父装配工作文档中查找目标直接子节点记录。
 *
 * 设计说明：局部版本切换时，新 CadNode 只代表被引用版本的内容；当前装配中的 occurrence 身份、
 * 显示名、节点类型与采纳版本指针应以父装配 `_assembly.json` 的 direct child 记录为准。
 *
 * @param documents 当前加载结果与调用方工作文档覆盖合并后的装配文档索引。
 * @param parentNodeKey 目标子节点所属父装配 nodeKey。
 * @param targetNodeKey 当前装配关系中稳定的目标子节点 nodeKey。
 * @returns 父装配工作文档中的直接子节点记录；没有文档或没有对应节点时返回 null。
 */
function resolveAssemblyDocumentDirectChildNode(
  documents: ReadonlyMap<string, AssemblyDocument>,
  parentNodeKey: string,
  targetNodeKey: string,
): AssemblyDocumentNode | null {
  const document = documents.get(parentNodeKey)

  if (!document) {
    return null
  }

  return listAssemblyDocumentDirectChildNodes(document, parentNodeKey)
    .find((node) => node.nodeKey === targetNodeKey) ?? null
}

/**
 * 将父装配 direct child 的 occurrence 身份应用到新映射出的替换根节点。
 *
 * 设计说明：新模型包负责提供几何、包文件与子树内容；父装配负责定义“这个位置上的实例是谁”。
 * 若没有工作文档记录，则退回当前节点身份，避免后端快照的实例 UUID 让后续切换误判为新增实例。
 *
 * @param node 新 CadNode 映射并重定向 key 后得到的模型节点。
 * @param documentNode 父装配工作文档中的目标直接子节点记录。
 * @param currentNode 当前运行态设计树中的旧节点，可作为无工作文档时的身份兜底。
 * @returns 已保留父装配实例身份的模型节点。
 */
function applyAssemblyDocumentDirectChildIdentity(
  node: ModelAssetNode,
  documentNode: AssemblyDocumentNode | null,
  currentNode?: ModelAssetNode,
): ModelAssetNode {
  const documentRepositoryId = documentNode?.repositoryId.trim()
  const documentCommitId = documentNode?.commitId.trim()
  const nextRepositoryId = documentRepositoryId || node.repositoryId
  const nextCommitId = documentCommitId || node.commitId

  return {
    ...node,
    displayName: documentNode?.instanceName?.trim() || currentNode?.displayName?.trim() || node.displayName,
    instanceUuid: documentNode?.instanceId?.trim() || currentNode?.instanceUuid?.trim() || node.instanceUuid,
    repositoryId: nextRepositoryId,
    branchId: documentNode?.branchId?.trim() ?? node.branchId,
    commitId: nextCommitId,
    versionKey: `${nextRepositoryId}:${nextCommitId}`,
    nodeType: documentNode?.nodeType ?? node.nodeType,
  }
}

/**
 * 将 working-only 一级子节点转成只用于 key 派生的模型资产占位。
 *
 * @param node working 装配文档中的直接子节点。
 * @param parentNodeKey 根装配 nodeKey。
 * @returns 不参与加载的模型资产占位节点。
 */
function createWorkingOnlyChildModelAssetNode(node: AssemblyDocumentNode, parentNodeKey: string): ModelAssetNode {
  return {
    nodeKey: node.nodeKey,
    displayName: node.instanceName ?? node.nodeKey,
    instanceUuid: node.instanceId ?? '',
    parentNodeKey,
    depth: 1,
    repositoryId: node.repositoryId,
    branchId: node.branchId ?? '',
    commitId: node.commitId,
    versionKey: `${node.repositoryId}:${node.commitId}`,
    versionSwitchable: true,
    nodeType: node.nodeType,
    localTransform: [...node.transform],
    boundingBox: [],
    packageFiles: [],
    children: [],
  }
}

/**
 * 创建包含 working-only 子节点占位的根节点，用于稳定派生新增空节点 key。
 *
 * @param input 当前模型结果与 working 文档。
 * @returns 仅用于 key 派生的根节点快照。
 */
function createWorkingAwareRootForEmptyChildKey(input: CreateEmptyChildWorkingAssemblyDocumentInput): ModelAssetNode {
  const parentNodeKey = input.currentResult.root.nodeKey
  const loadedChildNodeKeys = new Set(input.currentResult.root.children.map((child) => child.nodeKey))
  const workingOnlyChildren = listAssemblyDocumentDirectChildNodes(
    input.workingAssemblyDocument,
    parentNodeKey,
  )
    .filter((node) => !loadedChildNodeKeys.has(node.nodeKey))
    .map((node) => createWorkingOnlyChildModelAssetNode(node, parentNodeKey))

  return {
    ...input.currentResult.root,
    children: [...input.currentResult.root.children, ...workingOnlyChildren],
  }
}

/**
 * 解析补入缺失一级节点时使用的原点放置矩阵。
 *
 * @param matrix working 文档中记录的可选矩阵。
 * @param context 错误定位上下文。
 * @returns 有效矩阵或单位矩阵副本。
 */
function resolveAppendMissingNodeTransform(matrix: readonly number[] | undefined, context: string): AssemblyTransform {
  if (!matrix) {
    return toAssemblyTransform(IDENTITY_TRANSFORM, `${context} identity fallback`)
  }

  return toAssemblyTransform(matrix, context)
}

/**
 * 创建缺失一级节点的本地插入快照输入。
 */
interface CreateMissingFirstLevelNodeVersionSnapshotInput {
  /**
   * 当前项目模型加载结果。
   */
  readonly currentResult: ProjectModelLoadResult
  /**
   * 当前装配关系中稳定的目标节点 key。
   */
  readonly targetNodeKey: string
  /**
   * 父装配文档记录的 direct child occurrence 摘要。
   */
  readonly documentNode: AssemblyDocumentNode | null
  /**
   * 目标节点在父装配下应使用的放置矩阵。
   */
  readonly replacementRootTransform?: readonly number[]
  /**
   * 采纳版本对应的完整后端节点快照。
   */
  readonly nextChild: CadNode
  /** 与后端节点快照同一响应返回的总归档包。 */
  readonly archiveFile?: PuppyCADWeb.CadArchiveFileSnapshot
  /**
   * 后端服务根地址。
   */
  readonly backendBaseUrl: string
  /**
   * 当前节点在父装配 direct children 中的顺序。
   */
  readonly siblingIndex?: number
  /** 当前 repository bridge 已明确记录的 occurrence 挂载身份。 */
  readonly mountRuntimeAssemblyNodeId?: string | null
}

/**
 * 使用已映射模型节点创建缺失一级节点的本地插入快照输入。
 */
interface CreateMissingFirstLevelModelNodeVersionSnapshotInput {
  /**
   * 当前项目模型加载结果。
   */
  readonly currentResult: ProjectModelLoadResult
  /**
   * 当前装配关系中稳定的目标节点 key。
   */
  readonly targetNodeKey: string
  /**
   * 父装配文档记录的 direct child occurrence 摘要。
   */
  readonly documentNode: AssemblyDocumentNode | null
  /**
   * 目标节点在父装配下应使用的放置矩阵。
   */
  readonly replacementRootTransform?: readonly number[]
  /**
   * 已经映射好的后端节点快照。
   */
  readonly nextNode: ModelAssetNode
  /** 当前 repository bridge 已明确记录的 occurrence 挂载身份。 */
  readonly mountRuntimeAssemblyNodeId?: string | null
}

/**
 * 缺失一级节点的本地插入快照。
 */
interface MissingFirstLevelNodeVersionSnapshot {
  /**
   * 已经追加新节点的本地 root 快照。
   */
  readonly root: ModelAssetNode
  /**
   * 本次追加的节点 key。
   */
  readonly insertedNodeKey: string
}

/**
 * 将一个缺失一级节点追加到 root.children。
 *
 * @param root 当前 root 快照。
 * @param insertedNode 已经完成 key、身份和 transform 收口的新节点。
 * @returns 追加新节点后的 root 快照。
 * @throws 新节点的结构 key 或 assemblyNodeId 已被直属 child 占用时抛出。
 */
function appendMissingFirstLevelNodeToRoot(root: ModelAssetNode, insertedNode: ModelAssetNode): ModelAssetNode {
  if (root.children.some((child) => child.nodeKey === insertedNode.nodeKey)) {
    throw new Error(`缺失一级节点补入发生 key 冲突：nodeKey=${insertedNode.nodeKey}`)
  }

  const insertedAssemblyNodeId = resolveProjectModelNodeAssemblyNodeId(insertedNode)
  const occupiedNode = root.children.find((child) => (
    resolveProjectModelNodeAssemblyNodeId(child) === insertedAssemblyNodeId
  ))
  if (occupiedNode) {
    // 自动补入属于持久化结构写入；必须在 append 边界拒绝身份占用，避免把冲突延迟到提交阶段。
    throw new Error(
      '缺失一级节点补入发生 assemblyNodeId 冲突：'
      + `assemblyNodeId=${insertedAssemblyNodeId}, existingNodeKey=${occupiedNode.nodeKey}, `
      + `insertedNodeKey=${insertedNode.nodeKey}`,
    )
  }

  return {
    ...root,
    children: [...root.children, insertedNode],
  }
}

/**
 * 使用已映射模型节点创建缺失一级节点的本地插入快照。
 *
 * 设计说明：该函数是旧单节点补入和 root `.puppy` 加载期批量补入的共同收口点，
 * 确保 key 重定向、父装配 occurrence 身份、采纳版本和 transform 的处理完全一致。
 *
 * @param input 已映射模型节点与采纳版本上下文。
 * @returns 已经把新节点追加到 root 的本地快照。
 */
function createMissingFirstLevelModelNodeVersionSnapshot(
  input: CreateMissingFirstLevelModelNodeVersionSnapshotInput,
): MissingFirstLevelNodeVersionSnapshot {
  const nextNodeWithRebasedKey = rebaseReplacementRootNodeKey(input.nextNode, input.targetNodeKey)
  const placedNode: ModelAssetNode = {
    ...applyAssemblyDocumentDirectChildIdentity(nextNodeWithRebasedKey, input.documentNode),
    localTransform: resolveAppendMissingNodeTransform(
      input.replacementRootTransform,
      input.targetNodeKey,
    ),
  }
  // 只有项目根直属 child 满足一级 occurrence 契约；嵌套加载单元继续保留自身深度。
  const nextNode = input.currentResult.root.depth === 0
    ? mountLoadedSubtreeOccurrenceIdentities(placedNode, {
        runtimeAssemblyNodeId: resolveMissingFirstLevelNodeAssemblyNodeId({
          parentNode: input.currentResult.root,
          targetNodeKey: input.targetNodeKey,
          recoveredAssemblyNodeId: input.mountRuntimeAssemblyNodeId,
        }),
        nodeKey: input.targetNodeKey,
        parentNodeKey: input.currentResult.root.nodeKey,
        depth: 1,
        localTransform: placedNode.localTransform,
      })
    : placedNode

  return {
    root: appendMissingFirstLevelNodeToRoot(input.currentResult.root, nextNode),
    insertedNodeKey: input.targetNodeKey,
  }
}

/**
 * 创建缺失一级节点的本地插入快照。
 *
 * 设计说明：后端重载得到的 CadNode 先映射为统一 ModelAssetNode，再进入同一个本地插入收口点。
 * 这样所有被公共策略允许扩展出来的 direct child 都只依赖 GetNodeTree 权威快照。
 *
 * @param input 缺失一级节点与采纳版本上下文。
 * @returns 已经把新节点追加到 root 的本地快照。
 */
function createMissingFirstLevelNodeVersionSnapshot(
  input: CreateMissingFirstLevelNodeVersionSnapshotInput,
): MissingFirstLevelNodeVersionSnapshot {
  const mappedNode = mapCadNodeToModelAssetNode({
    cadNode: normalizeNodeVersionSwitchCadNodeSnapshot(input.nextChild),
    ...(input.archiveFile ? { archiveFile: input.archiveFile } : {}),
    backendBaseUrl: input.backendBaseUrl,
    parentNodeKey: input.currentResult.root.nodeKey,
    depth: input.currentResult.root.depth + 1,
    siblingIndex: input.siblingIndex ?? input.currentResult.root.children.length,
  })

  return createMissingFirstLevelModelNodeVersionSnapshot({
    currentResult: input.currentResult,
    targetNodeKey: input.targetNodeKey,
    documentNode: input.documentNode,
    replacementRootTransform: input.replacementRootTransform,
    nextNode: mappedNode,
    mountRuntimeAssemblyNodeId: input.mountRuntimeAssemblyNodeId,
  })
}

/**
 * 定位目标子树的父节点上下文。
 *
 * @param root 当前模型资产树根节点。
 * @param targetNodeKey 需要替换的目标节点 key。
 * @returns 目标节点父节点、深度和兄弟序号。
 */
function resolveProjectModelSubtreeLocation(
  root: ModelAssetNode,
  targetNodeKey: string,
): ProjectModelSubtreeLocation {
  for (let childIndex = 0; childIndex < root.children.length; childIndex += 1) {
    const child = root.children[childIndex]

    if (!child) {
      continue
    }
    if (child.nodeKey === targetNodeKey) {
      return {
        node: child,
        parentNodeKey: root.nodeKey,
        depth: child.depth,
        siblingIndex: childIndex,
      }
    }
    const childLocation = resolveProjectModelSubtreeLocation(child, targetNodeKey)

    if (childLocation.parentNodeKey) {
      return childLocation
    }
  }

  return {
    node: root,
    parentNodeKey: '',
    depth: 0,
    siblingIndex: 0,
  }
}

/**
 * 不可变替换模型资产树中的指定子树。
 *
 * @param root 当前递归入口节点。
 * @param targetNodeKey 需要替换的目标节点 key。
 * @param nextNode 替换后的节点。
 * @returns 替换后的当前递归入口节点。
 */
function replaceProjectModelSubtree(
  root: ModelAssetNode,
  targetNodeKey: string,
  nextNode: ModelAssetNode,
): ModelAssetNode {
  if (root.nodeKey === targetNodeKey) {
    return nextNode
  }

  let changed = false
  const children = root.children.map((child) => {
    const nextChild = replaceProjectModelSubtree(child, targetNodeKey, nextNode)

    if (nextChild !== child) {
      changed = true
    }

    return nextChild
  })

  return changed
    ? {
      ...root,
      children,
    }
    : root
}

/**
 * 递归重定向替换子树的 key 前缀。
 *
 * @param node 当前递归节点。
 * @param oldPrefix 后端快照映射出的旧 key 前缀。
 * @param nextPrefix 当前装配关系中应保留的新 key 前缀。
 * @param parentNodeKey 重定向后的父节点 key。
 * @returns key 与 parentNodeKey 已对齐到当前结构位置的新节点。
 */
function rebaseReplacementSubtreeKeyPrefix(
  node: ModelAssetNode,
  oldPrefix: string,
  nextPrefix: string,
  parentNodeKey: string | undefined,
): ModelAssetNode {
  const nodeKey = node.nodeKey === oldPrefix || node.nodeKey.startsWith(`${oldPrefix}/`)
    ? `${nextPrefix}${node.nodeKey.slice(oldPrefix.length)}`
    : node.nodeKey

  return {
    ...node,
    nodeKey,
    parentNodeKey,
    children: node.children.map((child) => rebaseReplacementSubtreeKeyPrefix(
      child,
      oldPrefix,
      nextPrefix,
      nodeKey,
    )),
  }
}

/**
 * 重定向替换子树根节点的结构 key。
 *
 * 版本切换不应改变当前装配关系在设计树中的 key；若后端快照的实例身份变化，
 * 这里会将整棵替换子树的 key 前缀对齐到原 targetNodeKey，避免后代节点悬挂在旧前缀下。
 *
 * @param node 后端新版本映射出的模型资产节点。
 * @param targetNodeKey 当前树中的目标节点 key。
 * @returns key 已对齐到当前结构位置的新节点。
 */
function rebaseReplacementRootNodeKey(node: ModelAssetNode, targetNodeKey: string): ModelAssetNode {
  return rebaseReplacementSubtreeKeyPrefix(node, node.nodeKey, targetNodeKey, node.parentNodeKey)
}

/**
 * 解析版本切换后替换根节点应使用的放置矩阵。
 *
 * 版本切换只替换该实例引用的几何版本，不能让新版本快照自带的根 transform 覆盖父装配中的实例位置。
 *
 * @param input 版本切换输入。
 * @param currentNode 当前父装配中的目标节点。
 * @returns 应写入替换根节点的局部矩阵。
 */
function resolveReplacementRootTransform(
  input: Pick<CreateSwitchFirstLevelNodeVersionLoadUnitInput, 'replacementRootTransform'>,
  currentNode: ModelAssetNode,
): readonly number[] {
  const requestedTransform = input.replacementRootTransform

  if (requestedTransform?.length === 16) {
    return [...requestedTransform]
  }

  return [...currentNode.localTransform]
}

/**
 * 判断一个 scene binding 是否属于指定子树。
 *
 * @param binding 场景对象绑定。
 * @param nodeKey 子树根节点 key。
 * @returns binding 属于该子树时返回 true。
 */
function isSceneBindingInsideSubtree(binding: ProjectModelSceneBinding, nodeKey: string): boolean {
  return binding.nodeKey === nodeKey || binding.nodeKey.startsWith(`${nodeKey}/`)
}

/**
 * 合并多个装配文档索引，后传入的索引拥有更高优先级。
 *
 * @param sources 按优先级从低到高排列的装配文档索引。
 * @returns 合并后的装配文档索引副本。
 */
function mergeAssemblyDocumentSources(
  ...sources: readonly (ReadonlyMap<string, AssemblyDocument> | undefined)[]
): ReadonlyMap<string, AssemblyDocument> {
  const merged = new Map<string, AssemblyDocument>()

  sources.forEach((source) => {
    source?.forEach((document, nodeKey) => {
      merged.set(nodeKey, document)
    })
  })

  return merged
}

/**
 * 校验并定位可执行一级节点版本切换的目标子树。
 *
 * @param input 版本切换输入。
 * @returns 当前模型树中的目标子树位置。
 */
function resolveSwitchFirstLevelNodeVersionLocation(
  input: Pick<CreateSwitchFirstLevelNodeVersionLoadUnitInput, 'currentResult' | 'targetNodeKey'>,
): ProjectModelSubtreeLocation {
  const location = resolveProjectModelSubtreeLocation(input.currentResult.root, input.targetNodeKey)

  if (!location.parentNodeKey) {
    throw new Error(`项目模型子节点不存在，无法切换版本：targetNodeKey=${input.targetNodeKey}`)
  }
  if (location.depth !== 1) {
    throw new Error(`仅支持切换一级子节点版本：targetNodeKey=${input.targetNodeKey}, depth=${location.depth}`)
  }

  return location
}

/**
 * 创建一级节点版本切换的目标子树加载单元。
 *
 * @param input 当前模型、目标节点 key 与后端新版本快照。
 * @returns 只包含目标子树根节点的加载单元。
 */
export function createSwitchFirstLevelNodeVersionLoadUnit(
  input: CreateSwitchFirstLevelNodeVersionLoadUnitInput,
): ModelAssetNode {
  const { node, archiveFile } = input.nextChildSnapshot
  const location = resolveSwitchFirstLevelNodeVersionLocation(input)
  const assemblyDocumentsByNodeKey = mergeAssemblyDocumentOverrides(
    input.currentResult.assemblyDocumentsByNodeKey,
    input.assemblyDocumentOverridesByNodeKey,
  )
  const documentNode = resolveAssemblyDocumentDirectChildNode(
    assemblyDocumentsByNodeKey,
    location.parentNodeKey,
    input.assemblyDocumentNodeKey ?? input.targetNodeKey,
  )
  const nextNodeWithRebasedKey = rebaseReplacementRootNodeKey(
    mapCadNodeToModelAssetNode({
      cadNode: normalizeNodeVersionSwitchCadNodeSnapshot(node),
      ...(archiveFile ? { archiveFile } : {}),
      backendBaseUrl: input.backendBaseUrl,
      parentNodeKey: location.parentNodeKey,
      depth: location.depth,
      siblingIndex: location.siblingIndex,
    }),
    input.targetNodeKey,
  )

  const placedNode: ModelAssetNode = {
    ...applyAssemblyDocumentDirectChildIdentity(nextNodeWithRebasedKey, documentNode, location.node),
    localTransform: resolveReplacementRootTransform(input, location.node),
  }

  // 版本切换只替换 occurrence 引用的内容；发布新子树前必须恢复旧槽位的运行身份与放置。
  return mountLoadedSubtreeOccurrenceIdentities(placedNode, {
    runtimeAssemblyNodeId: projectModelRuntimeStateResolver.resolveRuntimeAssemblyNodeId(
      input.currentResult.runtimeState, input.targetNodeKey, input.assemblyDocumentNodeKey,
    )
      ?? resolveProjectModelNodeAssemblyNodeId(location.node),
    nodeKey: input.targetNodeKey,
    parentNodeKey: location.parentNodeKey,
    depth: 1,
    localTransform: placedNode.localTransform,
  })
}

/**
 * 从缺失节点快照中取出本次插入的一级节点。
 *
 * @param snapshot 缺失一级节点补加载快照。
 * @returns 插入节点本身。
 */
function resolveInsertedMissingFirstLevelNode(
  snapshot: MissingFirstLevelNodeVersionSnapshot,
): ModelAssetNode {
  const insertedNode = snapshot.root.children.find((child) => child.nodeKey === snapshot.insertedNodeKey)

  if (!insertedNode) {
    throw new Error(`缺失一级节点快照缺少插入节点：nodeKey=${snapshot.insertedNodeKey}`)
  }

  return insertedNode
}

/**
 * 创建缺失一级节点版本补加载的目标子树加载单元。
 *
 * @param input 当前模型、working 稳定 key 与采纳版本快照。
 * @returns 只包含待插入一级节点的加载单元。
 */
export function createAppendMissingFirstLevelNodeVersionLoadUnit(
  input: CreateAppendMissingFirstLevelNodeVersionLoadUnitInput,
): ModelAssetNode {
  assertAppendMissingFirstLevelNodeVersionInput(input)
  const { node, archiveFile } = input.nextChildSnapshot

  const assemblyDocumentsByNodeKey = mergeAssemblyDocumentOverrides(
    input.currentResult.assemblyDocumentsByNodeKey,
    input.assemblyDocumentOverridesByNodeKey,
  )
  const documentNode = resolveAssemblyDocumentDirectChildNode(
    assemblyDocumentsByNodeKey,
    input.currentResult.root.nodeKey,
    input.assemblyDocumentNodeKey ?? input.targetNodeKey,
  )
  const snapshot = createMissingFirstLevelNodeVersionSnapshot({
    currentResult: input.currentResult,
    targetNodeKey: input.targetNodeKey,
    documentNode,
    replacementRootTransform: input.replacementRootTransform,
    mountRuntimeAssemblyNodeId: projectModelRuntimeStateResolver.resolveRuntimeAssemblyNodeId(
      input.currentResult.runtimeState, input.targetNodeKey, input.assemblyDocumentNodeKey,
    ),
    nextChild: node,
    ...(archiveFile ? { archiveFile } : {}),
    backendBaseUrl: input.backendBaseUrl,
  })

  return resolveInsertedMissingFirstLevelNode(snapshot)
}

/**
 * 创建一级节点版本子树独立加载单元。
 *
 * @param input 父装配上下文、目标节点 key 与后端新版本快照。
 * @returns 只包含目标子树根节点的加载单元。
 */
export function createFirstLevelNodeVersionSubtreeLoadUnit(
  input: CreateFirstLevelNodeVersionSubtreeLoadUnitInput,
): ModelAssetNode {
  const { node, archiveFile } = input.nextChildSnapshot
  const assemblyDocumentsByNodeKey = input.assemblyDocumentOverridesByNodeKey ?? new Map<string, AssemblyDocument>()
  const documentNode = resolveAssemblyDocumentDirectChildNode(
    assemblyDocumentsByNodeKey,
    input.parentNodeKey,
    input.targetNodeKey,
  )
  const mappedNode = mapCadNodeToModelAssetNode({
    cadNode: normalizeNodeVersionSwitchCadNodeSnapshot(node),
    ...(archiveFile ? { archiveFile } : {}),
    backendBaseUrl: input.backendBaseUrl,
    parentNodeKey: input.parentNodeKey,
    depth: 1,
    siblingIndex: input.siblingIndex,
  })

  return {
    ...applyAssemblyDocumentDirectChildIdentity(
      rebaseReplacementRootNodeKey(mappedNode, input.targetNodeKey),
      documentNode,
    ),
    localTransform: input.replacementRootTransform.length === 16
      ? [...input.replacementRootTransform]
      : [...IDENTITY_TRANSFORM],
  }
}

/**
 * 校验 prepared 子树根节点确实对应调用方声明的目标节点。
 *
 * @param input 目标 key 与 prepared 子树结果。
 * @param context 错误定位上下文。
 */
function assertPreparedSubtreeRootNodeKey(input: {
  readonly targetNodeKey: string
  readonly preparedSubtreeResult: ProjectModelLoadResult
}, context: string): void {
  const preparedRootNodeKey = input.preparedSubtreeResult.root.nodeKey

  if (preparedRootNodeKey !== input.targetNodeKey) {
    throw new Error(
      `prepared 子树根节点 key 与目标节点不一致：context=${context}, `
      + `targetNodeKey=${input.targetNodeKey}, preparedRootNodeKey=${preparedRootNodeKey}`,
    )
  }
}

/**
 * 校验 prepared append 结果可以作为当前 root 的一级子节点追加。
 *
 * @param input append merge 输入。
 */
function assertPreparedAppendMissingFirstLevelNodeRoot(
  input: MergePreparedAppendMissingFirstLevelNodeVersionResultInput,
): void {
  assertPreparedSubtreeRootNodeKey(input, 'append-missing-first-level-node-version')

  const preparedRoot = input.preparedSubtreeResult.root
  const expectedParentNodeKey = input.currentResult.root.nodeKey

  if (preparedRoot.parentNodeKey !== expectedParentNodeKey) {
    throw new Error(
      'prepared 待追加子树父节点不属于当前 root：'
      + `targetNodeKey=${input.targetNodeKey}, expectedParentNodeKey=${expectedParentNodeKey}, `
      + `preparedParentNodeKey=${preparedRoot.parentNodeKey ?? ''}`,
    )
  }
  if (preparedRoot.depth !== 1) {
    throw new Error(
      'prepared 待追加子树不是 root 一级子节点：'
      + `targetNodeKey=${input.targetNodeKey}, preparedDepth=${preparedRoot.depth}`,
    )
  }
}

/**
 * 合并已 prepared 的一级节点版本切换结果。
 *
 * @param input 当前结果、prepared 子树结果与装配文档覆盖索引。
 * @returns 替换目标子树后的完整项目模型结果。
 */
export function mergePreparedSwitchFirstLevelNodeVersionResult(
  input: MergePreparedSwitchFirstLevelNodeVersionResultInput,
): ProjectModelLoadResult {
  const location = resolveSwitchFirstLevelNodeVersionLocation(input)
  assertPreparedSubtreeRootNodeKey(input, 'switch-first-level-node-version')
  // prepare 可能从新 `.puppy` 物化 source-only descendants，发布前再次复用同一挂载规则补齐运行身份。
  const mountedPreparedRoot = mountLoadedSubtreeOccurrenceIdentities(
    input.preparedSubtreeResult.root,
    {
      runtimeAssemblyNodeId: projectModelRuntimeStateResolver.resolveRuntimeAssemblyNodeId(
        input.currentResult.runtimeState, input.targetNodeKey, input.assemblyDocumentNodeKey,
      )
        ?? resolveProjectModelNodeAssemblyNodeId(location.node),
      nodeKey: input.targetNodeKey,
      parentNodeKey: location.parentNodeKey,
      depth: 1,
      localTransform: input.preparedSubtreeResult.root.localTransform,
    },
  )

  const root = replaceProjectModelSubtree(
    input.currentResult.root,
    input.targetNodeKey,
    mountedPreparedRoot,
  )
  const sceneBindings = [
    ...input.currentResult.sceneBindings.filter(
      (binding) => !isSceneBindingInsideSubtree(binding, input.targetNodeKey),
    ),
    ...input.preparedSubtreeResult.sceneBindings,
  ]
  const runtimeState = projectModelRuntimeStateResolver.replaceSubtree(
    input.currentResult.runtimeState,
    input.preparedSubtreeResult.runtimeState,
    input.targetNodeKey,
  )

  return {
    root,
    designTree: buildProjectModelDesignTreeProjection({ root, sceneBindings }),
    sceneBindings,
    runtimeState,
    assemblyDocumentsByNodeKey: mergeAssemblyDocumentSources(
      input.currentResult.assemblyDocumentsByNodeKey,
      input.assemblyDocumentOverridesByNodeKey,
      input.assemblyDocumentsByNodeKey,
      input.preparedSubtreeResult.assemblyDocumentsByNodeKey,
    ),
  }
}

/**
 * 合并已 prepared 的缺失一级节点补加载结果。
 *
 * @param input 当前结果、prepared 插入节点结果与装配文档覆盖索引。
 * @returns 追加新一级节点后的完整项目模型结果。
 */
export function mergePreparedAppendMissingFirstLevelNodeVersionResult(
  input: MergePreparedAppendMissingFirstLevelNodeVersionResultInput,
): ProjectModelSubtreeMergeResult {
  assertAppendMissingFirstLevelNodeVersionInput(input)
  assertPreparedAppendMissingFirstLevelNodeRoot(input)

  const root = appendMissingFirstLevelNodeToRoot(
    input.currentResult.root,
    input.preparedSubtreeResult.root,
  )
  const sceneBindings = [
    ...input.currentResult.sceneBindings,
    ...input.preparedSubtreeResult.sceneBindings,
  ]
  const runtimeState = projectModelRuntimeStateResolver.append(
    input.currentResult.runtimeState,
    input.preparedSubtreeResult.runtimeState,
  )

  return {
    root,
    designTree: buildProjectModelDesignTreeProjection({ root, sceneBindings }),
    sceneBindings,
    runtimeState,
    assemblyDocumentsByNodeKey: mergeAssemblyDocumentSources(
      input.currentResult.assemblyDocumentsByNodeKey,
      input.assemblyDocumentOverridesByNodeKey,
      input.assemblyDocumentsByNodeKey,
      input.preparedSubtreeResult.assemblyDocumentsByNodeKey,
    ),
    insertedNodeKey: input.targetNodeKey,
  }
}

/**
 * 返回已 prepared 的一级节点版本独立子树结果。
 *
 * @param input prepared 子树结果与可选装配文档覆盖索引。
 * @returns 与 prepared 子树等价的加载结果。
 */
export function mergePreparedFirstLevelNodeVersionSubtreeResult(
  input: MergePreparedFirstLevelNodeVersionSubtreeResultInput,
): ProjectModelLoadResult {
  assertPreparedSubtreeRootNodeKey(input, 'first-level-node-version-subtree')

  return {
    ...input.preparedSubtreeResult,
    assemblyDocumentsByNodeKey: mergeAssemblyDocumentSources(
      input.assemblyDocumentOverridesByNodeKey,
      input.assemblyDocumentsByNodeKey,
      input.preparedSubtreeResult.assemblyDocumentsByNodeKey,
    ),
  }
}

/**
 * 从 OpenProject/GetCadNodeTree 返回的 CadNode 加载完整项目模型。
 *
 * 设计说明：web-client 只负责把后端 CadNode 映射为 cad-core 可消费的资产树，
 * 真正的读取顺序、事务边界和设计树投影都交给 cad-core 的加载计划与执行器收口。
 *
 * @param input 后端 CAD 节点、版本文件读取器和下载上下文。
 * @returns cad-core 项目模型加载结果。
 */
export async function loadProjectModelFromCadNode(
  input: LoadProjectModelFromCadNodeInput,
): Promise<LoadProjectModelFromCadNodeResult> {
  const root = mapCadNodeToModelAssetNode({
    cadNode: input.cadNode,
    ...(input.archiveFile ? { archiveFile: input.archiveFile } : {}),
    backendBaseUrl: input.backendBaseUrl,
    depth: 0,
    siblingIndex: 0,
  })
  const plan = createProjectModelLoadPlan({ kind: 'root-load', root })

  if (input.nodeReloadAdapter) {
    // preparer 依赖本模块的补树函数，旧 service 入口用动态导入避免静态模块环。
    const { prepareProjectModelBackendLoad } = await import('./project-model-backend-load-preparer')
    const preparedLoad = await prepareProjectModelBackendLoad({
      planInput: { kind: 'root-load', root },
      reader: input.reader,
      fileCache: input.fileCache ?? createEmptyProjectModelFileByteCacheAdapter(),
      nodeReloadAdapter: input.nodeReloadAdapter,
      backendBaseUrl: input.backendBaseUrl,
      signal: input.signal,
    })

    return preparedLoad.loadResult
  }

  return executeProjectModelLoad({
    root,
    reader: input.reader,
    signal: input.signal,
    plan,
  })
}

/**
 * 从 OpenProject 响应加载项目模型。
 *
 * @param input OpenProject 响应、下载上下文和读取器。
 * @returns cad-core 项目模型加载结果。
 */
export async function loadOpenProject(input: LoadOpenProjectInput): Promise<ProjectModelLoadResult> {
  if (!input.openProjectResponse.project) {
    throw new Error(`OpenProject 未返回项目模型根节点：projectId=${input.openProjectResponse.projectId.toString()}`)
  }
  const archiveFile = new CadArchiveFileSnapshotMapper().map(
    input.openProjectResponse.archiveFile,
    `OpenProject projectId=${input.openProjectResponse.projectId.toString()}`,
  )

  return loadRootSnapshot({
    rootNode: input.openProjectResponse.project,
    ...(archiveFile
      ? { archiveFile }
      : {}),
    backendBaseUrl: input.backendBaseUrl,
    reader: input.reader,
    ...(input.nodeReloadAdapter ? { nodeReloadAdapter: input.nodeReloadAdapter } : {}),
    ...(input.fileCache ? { fileCache: input.fileCache } : {}),
    signal: input.signal,
  })
}

/**
 * 从根节点快照加载项目模型。
 *
 * @param input 根节点快照、下载上下文和读取器。
 * @returns cad-core 项目模型加载结果。
 */
export async function loadRootSnapshot(input: LoadRootSnapshotInput): Promise<ProjectModelLoadResult> {
  return loadProjectModelFromCadNode({
    cadNode: input.rootNode,
    ...(input.archiveFile ? { archiveFile: input.archiveFile } : {}),
    backendBaseUrl: input.backendBaseUrl,
    reader: input.reader,
    ...(input.nodeReloadAdapter ? { nodeReloadAdapter: input.nodeReloadAdapter } : {}),
    ...(input.fileCache ? { fileCache: input.fileCache } : {}),
    signal: input.signal,
  })
}

/**
 * 切换一级子节点版本并只加载目标子树。
 *
 * @param input 当前模型结果、新版本节点快照和读取器。
 * @returns 替换目标子树后的项目模型加载结果。
 */
export async function switchFirstLevelNodeVersion(
  input: SwitchFirstLevelNodeVersionInput,
): Promise<ProjectModelLoadResult> {
  const location = resolveProjectModelSubtreeLocation(input.currentResult.root, input.targetNodeKey)

  if (!location.parentNodeKey) {
    throw new Error(`项目模型子节点不存在，无法切换版本：targetNodeKey=${input.targetNodeKey}`)
  }
  if (location.depth !== 1) {
    throw new Error(`仅支持切换一级子节点版本：targetNodeKey=${input.targetNodeKey}, depth=${location.depth}`)
  }

  const assemblyDocumentsByNodeKey = mergeAssemblyDocumentOverrides(
    input.currentResult.assemblyDocumentsByNodeKey,
    input.assemblyDocumentOverridesByNodeKey,
  )
  const nextNode = createSwitchFirstLevelNodeVersionLoadUnit(input)
  const root = replaceProjectModelSubtree(input.currentResult.root, input.targetNodeKey, nextNode)

  return executeProjectModelLoad({
    root,
    reader: input.reader,
    signal: input.signal,
    assemblyDocumentOverridesByNodeKey: assemblyDocumentsByNodeKey,
    sceneBindings: input.currentResult.sceneBindings.filter(
      (binding) => !isSceneBindingInsideSubtree(binding, input.targetNodeKey),
    ),
    plan: createProjectModelLoadPlan({
      kind: 'replace-subtree',
      root,
      targetNodeKey: input.targetNodeKey,
      parentNodeKey: location.parentNodeKey,
    }),
  })
}

/**
 * 校验待补入节点确实是当前根装配下缺失的一级子节点。
 *
 * @param input 补加载输入。
 */
function assertAppendMissingFirstLevelNodeVersionInput(
  input: Pick<CreateAppendMissingFirstLevelNodeVersionLoadUnitInput, 'currentResult' | 'targetNodeKey'>,
): void {
  const rootNodeKey = input.currentResult.root.nodeKey
  const rootPrefix = `${rootNodeKey}/`

  if (!input.targetNodeKey.startsWith(rootPrefix)) {
    throw new Error(`缺失子节点不属于当前根装配：targetNodeKey=${input.targetNodeKey}, rootNodeKey=${rootNodeKey}`)
  }

  const relativeNodeKey = input.targetNodeKey.slice(rootPrefix.length)

  if (!relativeNodeKey || relativeNodeKey.includes('/')) {
    throw new Error(`仅支持补入根装配一级子节点：targetNodeKey=${input.targetNodeKey}`)
  }

  const existingLocation = resolveProjectModelSubtreeLocation(input.currentResult.root, input.targetNodeKey)

  if (existingLocation.parentNodeKey) {
    throw new Error(`目标子节点已存在于项目模型中：targetNodeKey=${input.targetNodeKey}`)
  }
}

/**
 * 设计树缺失一级子节点时，按 working 中的结构 key 补加载采纳版本。
 *
 * 设计说明：创建空子节点后只写 workAssemble，不立即进入运行态设计树。后续用户选择采纳版本时，
 * 这里把该节点作为 root 的一级子节点补入，并在未提供位姿时按原点放置。
 *
 * @param input 当前模型结果、working 节点 key 和采纳版本快照。
 * @returns 补入节点并加载新版本后的项目模型结果。
 */
export async function appendMissingFirstLevelNodeVersion(
  input: AppendMissingFirstLevelNodeVersionInput,
): Promise<ProjectModelLoadResult> {
  assertAppendMissingFirstLevelNodeVersionInput(input)

  const assemblyDocumentsByNodeKey = mergeAssemblyDocumentOverrides(
    input.currentResult.assemblyDocumentsByNodeKey,
    input.assemblyDocumentOverridesByNodeKey,
  )
  const insertedNode = createAppendMissingFirstLevelNodeVersionLoadUnit(input)
  const root = appendMissingFirstLevelNodeToRoot(input.currentResult.root, insertedNode)
  const plan = createProjectModelLoadPlan({
    kind: 'insert-node',
    root,
    insertedNodeKey: input.targetNodeKey,
    parentNodeKey: input.currentResult.root.nodeKey,
  })

  return executeProjectModelLoad({
    root,
    reader: input.reader,
    signal: input.signal,
    sceneBindings: input.currentResult.sceneBindings,
    assemblyDocumentOverridesByNodeKey: assemblyDocumentsByNodeKey,
    plan,
  })
}

/**
 * 创建后端树扩展结果的空变更输出。
 *
 * @param root 当前 root 快照。
 * @returns 未发生补齐时的稳定返回结构。
 */
function createEmptyRootPuppyAssemblyMissingFirstLevelNodeSnapshotResult(
  root: ModelAssetNode,
): RootPuppyAssemblyMissingFirstLevelNodeSnapshotResult {
  return {
    root,
    insertedNodeKeys: [],
    skippedUnversionedNodeKeys: [],
    invalidNodeKeys: [],
  }
}

/**
 * 校验并取得可加载的装配 direct child 节点摘要。
 *
 * @param reference 装配文档中的宽松 direct child 引用。
 * @returns 可供插入快照消费的严格节点摘要。
 */
function resolveVersionedDirectChildNode(
  reference: AssemblyDocumentDirectChildReference,
): AssemblyDocumentNode {
  if (reference.status !== 'versioned' || !reference.node) {
    throw new Error(`后端树扩展节点缺少采纳版本摘要：nodeKey=${reference.nodeKey}`)
  }

  return reference.node
}

/**
 * 校验重载回来的 CadNode 是否仍然指向父装配记录的采纳版本。
 *
 * @param reference 父装配中保存的版本引用。
 * @param hydratedNode 后端重载回来的完整节点快照。
 */
function assertHydratedDirectChildVersion(
  reference: AssemblyDocumentDirectChildReference,
  hydratedNode: CadNode,
): void {
  const expectedRepositoryId = reference.repositoryId?.trim() ?? ''
  const expectedCommitId = reference.commitId?.trim() ?? ''
  const expectedBranchId = reference.branchId?.trim() ?? ''
  const actualRepositoryId = hydratedNode.repositoryId.toString()
  const actualCommitId = hydratedNode.commitId.trim()
  const actualBranchId = hydratedNode.branchId > 0n ? hydratedNode.branchId.toString() : ''

  if (actualRepositoryId !== expectedRepositoryId || actualCommitId !== expectedCommitId) {
    throw new Error(
      '后端树扩展节点版本不一致：'
      + `nodeKey=${reference.nodeKey}, expectedRepositoryId=${expectedRepositoryId}, `
      + `actualRepositoryId=${actualRepositoryId}, expectedCommitId=${expectedCommitId}, actualCommitId=${actualCommitId}`,
    )
  }
  if (expectedBranchId && actualBranchId && actualBranchId !== expectedBranchId) {
    throw new Error(
      '后端树扩展节点分支不一致：'
      + `nodeKey=${reference.nodeKey}, expectedBranchId=${expectedBranchId}, actualBranchId=${actualBranchId}`,
    )
  }
}

/**
 * 按父装配记录的采纳版本重载一个缺失 direct child。
 *
 * @param reference 父装配中缺失的有版本 direct child 引用。
 * @param nodeReloadAdapter 节点重载适配器。
 * @returns 后端返回的完整 CadNode 子树与可选总归档包快照。
 */
async function reloadVersionedDirectChildReference(
  reference: AssemblyDocumentDirectChildReference,
  nodeReloadAdapter: NodeReloadApiAdapter,
): Promise<CadNodeLoadSnapshot> {
  const repositoryId = reference.repositoryId?.trim() ?? ''
  const commitId = reference.commitId?.trim() ?? ''
  const branchId = reference.branchId?.trim() ?? ''

  if (!repositoryId || !commitId) {
    throw new Error(`后端树扩展节点缺少版本定位信息：nodeKey=${reference.nodeKey}`)
  }

  const reloadInput = {
    nodeKey: reference.nodeKey,
    repositoryId,
    ...(branchId ? { branchId } : {}),
    commitId,
  }
  const hydratedSnapshot = await nodeReloadAdapter.reloadSnapshot(reloadInput)

  assertHydratedDirectChildVersion(reference, hydratedSnapshot.node)
  return hydratedSnapshot
}

/**
 * 在当前 load root 允许后端树扩展时，根据 `_assembly.json` 补齐多出的 direct child 采纳版本节点。
 *
 * 设计说明：本函数服务的是“当前加载单元 root”，不是当前场景 root。命中 `.puppy` 的装配加载单元
 * 仍必须对比包内一级节点与 `_assembly.json` direct child；只有 `.puppy` 未覆盖且文档有采纳版本时，
 * 才通过 GetCadNodeTree 读取权威子树并把补齐后的 root 作为完整替换单元交给上层 apply。
 *
 * @param input 当前加载结果、后端地址与节点重载适配器。
 * @returns 已补齐 root 快照及诊断信息。
 * @internal 仅供同一 loading unit 的 expand 编排层跨 pass 传递事务状态。
 */
export async function createRootPuppyAssemblyMissingFirstLevelNodeSnapshotForInternalExpandTransaction(
  input: InternalCreateRootPuppyAssemblyMissingFirstLevelNodeSnapshotInput,
): Promise<InternalRootPuppyAssemblyMissingFirstLevelNodeSnapshotOutput> {
  const checkResult = await checkPuppyAssemblyDirectChildren({
    currentResult: input.currentResult,
    coverageContext: input.coverageContext ?? null,
    ...(input.completeGlbCoverage
      ? { completeGlbCoverage: input.completeGlbCoverage }
      : {}),
  })

  if (!checkResult.diff) {
    return {
      result: createEmptyRootPuppyAssemblyMissingFirstLevelNodeSnapshotResult(input.currentResult.root),
      internalTransactionState: createInternalPuppyAssemblyExpandTransactionState(
        input.internalTransactionState.insertedNodeKeyByReferenceLocator,
      ),
    }
  }

  const diff = checkResult.diff
  let root = input.currentResult.root
  const insertedNodeKeys: string[] = []
  let internalTransactionState = createInternalPuppyAssemblyExpandTransactionState(
    input.internalTransactionState.insertedNodeKeyByReferenceLocator,
  )

  for (const reference of diff.needsAddReferences.filter(reference => reference.status === 'versioned')) {
    const referenceLocator = createInternalPuppyAssemblyDirectChildReferenceLocator(
      input.currentResult.root.nodeKey,
      reference.nodeKey,
    )
    if (resolveReusableInternalPuppyAssemblyInsertedNodeKey({
      state: internalTransactionState,
      targetRoot: root,
      referenceLocator,
    })) {
      continue
    }

    const documentNode = resolveVersionedDirectChildNode(reference)
    const hydratedSnapshot = await reloadVersionedDirectChildReference(
      reference,
      input.nodeReloadAdapter,
    )
    const snapshot = createMissingFirstLevelNodeVersionSnapshot({
      currentResult: {
        ...input.currentResult,
        root,
      },
      targetNodeKey: reference.nodeKey,
      documentNode,
      replacementRootTransform: documentNode.transform,
      nextChild: hydratedSnapshot.node,
      ...(hydratedSnapshot.archiveFile
        ? { archiveFile: hydratedSnapshot.archiveFile }
        : {}),
      backendBaseUrl: input.backendBaseUrl,
      siblingIndex: reference.siblingIndex,
      // node_where 是导出期写入的 occurrence bridge；Set 只判断 coverage，不削弱该身份语义。
      mountRuntimeAssemblyNodeId: reference.nodeWhere && checkResult.packageFileId
        ? parseRepositoryPuppyNodeWhere(reference.nodeWhere, checkResult.packageFileId)
        : null,
    })

    root = snapshot.root
    insertedNodeKeys.push(snapshot.insertedNodeKey)
    internalTransactionState = recordInternalPuppyAssemblyInsertedNode({
      state: internalTransactionState,
      referenceLocator,
      insertedNodeKey: snapshot.insertedNodeKey,
    })
  }

  const insertedNodeKeySet = new Set(insertedNodeKeys)
  const transactionInsertedChildren: ModelAssetNode[] = []
  for (const reference of diff.directChildReferences) {
    const referenceLocator = createInternalPuppyAssemblyDirectChildReferenceLocator(
      input.currentResult.root.nodeKey,
      reference.nodeKey,
    )
    const insertedNodeKey = internalTransactionState.insertedNodeKeyByReferenceLocator.get(referenceLocator)
    if (!insertedNodeKey || !insertedNodeKeySet.has(insertedNodeKey)) {
      continue
    }

    const insertedChild = root.children.find(child => child.nodeKey === insertedNodeKey)
    if (!insertedChild) {
      throw new Error(
        `后端树扩展事务记录缺少已插入节点：locator=${referenceLocator}, nodeKey=${insertedNodeKey}`,
      )
    }
    transactionInsertedChildren.push(insertedChild)
  }
  const placedRoot: ModelAssetNode = {
    ...root,
    // 已存在 child 完整保留原槽位；本次 RPC 节点只按显式事务映射取得，并按文档引用顺序追加。
    children: [...input.currentResult.root.children, ...transactionInsertedChildren],
  }

  return {
    result: {
      root: placedRoot,
      insertedNodeKeys,
      skippedUnversionedNodeKeys: diff.needsAddReferences
        .filter(reference => reference.status === 'unversioned')
        .map(reference => reference.nodeKey),
      invalidNodeKeys: diff.needsAddReferences
        .filter(reference => reference.status === 'invalid')
        .map(reference => reference.nodeKey),
    },
    internalTransactionState: createInternalPuppyAssemblyExpandTransactionState(
      internalTransactionState.insertedNodeKeyByReferenceLocator,
    ),
  }
}

/**
 * 在单次调用边界内创建后端根扩展快照。
 *
 * 公共入口始终创建全新事务状态，既不接收也不返回 locator Map；跨 pass 去重只由内部 expand
 * 编排入口负责，避免调用方意外修改内部状态。
 *
 * @param input 当前加载结果、后端地址、覆盖上下文与节点重载适配器。
 * @returns 已补齐 root 快照及公开诊断信息。
 */
export async function createRootPuppyAssemblyMissingFirstLevelNodeSnapshot(
  input: CreateRootPuppyAssemblyMissingFirstLevelNodeSnapshotInput,
): Promise<RootPuppyAssemblyMissingFirstLevelNodeSnapshotResult> {
  const output = await createRootPuppyAssemblyMissingFirstLevelNodeSnapshotForInternalExpandTransaction({
    ...input,
    internalTransactionState: createInternalPuppyAssemblyExpandTransactionState(),
  })
  return output.result
}

/**
 * 只加载一级子节点的新版本子树。
 *
 * 设计说明：root `.puppy` 内嵌节点做版本切换时，运行态只需要拿到目标子树的新几何，
 * 不能为了得到一个可替换节点而重建整棵根项目。这里返回的 `root` 就是目标子节点本身。
 *
 * @param input 父装配上下文、目标子节点 key 和后端新版本快照。
 * @returns 目标子树项目模型加载结果。
 */
export async function loadFirstLevelNodeVersionSubtree(
  input: LoadFirstLevelNodeVersionSubtreeInput,
): Promise<ProjectModelLoadResult> {
  const assemblyDocumentsByNodeKey = input.assemblyDocumentOverridesByNodeKey ?? new Map<string, AssemblyDocument>()
  const root = createFirstLevelNodeVersionSubtreeLoadUnit(input)

  return executeProjectModelLoad({
    root,
    reader: input.reader,
    signal: input.signal,
    assemblyDocumentOverridesByNodeKey: assemblyDocumentsByNodeKey,
    plan: createProjectModelLoadPlan({
      kind: 'root-load',
      root,
    }),
  })
}

/**
 * 用 working 装配文档直接子节点重建可编辑的一级模型树。
 *
 * 设计说明：
 * 1. root `.puppy` 是提交后的显示快照，`root.children` 为空，不能作为局部版本切换边界；
 * 2. working `_assembly.json` 才保存当前 direct child 的稳定 nodeKey、实例 ID 和位姿；
 * 3. 本函数把 root 的旧 `.puppy` 包文件清掉，按 working direct child 重新装配子节点快照，
 *    让后续切换版本重新回到普通 project-model 一级子树替换链路。
 *
 * @param input 当前加载结果、working 文档和 direct child 后端快照。
 * @returns 按 working direct child 物化后的项目模型加载结果。
 */
export async function materializeWorkingAssemblyFirstLevelNodes(
  input: MaterializeWorkingAssemblyFirstLevelNodesInput,
): Promise<ProjectModelLoadResult> {
  const rootNodeKey = input.currentResult.root.nodeKey
  const workingChildren = listAssemblyDocumentDirectChildNodes(input.workingAssemblyDocument, rootNodeKey)
  const nextChildren = workingChildren.map((workingChild, siblingIndex) => {
    const snapshot = input.childSnapshotsByNodeKey.get(workingChild.nodeKey)

    if (!snapshot) {
      throw new Error(`working direct child 缺少后端节点快照：${workingChild.nodeKey}`)
    }

    const mappedNode = mapCadNodeToModelAssetNode({
      cadNode: normalizeNodeVersionSwitchCadNodeSnapshot(snapshot),
      backendBaseUrl: input.backendBaseUrl,
      parentNodeKey: rootNodeKey,
      depth: 1,
      siblingIndex,
    })
    const rebasedNode = rebaseReplacementRootNodeKey(mappedNode, workingChild.nodeKey)

    return {
      ...rebasedNode,
      displayName: workingChild.instanceName?.trim() || rebasedNode.displayName,
      instanceUuid: workingChild.instanceId?.trim() || rebasedNode.instanceUuid,
      repositoryId: workingChild.repositoryId,
      branchId: workingChild.branchId ?? rebasedNode.branchId,
      commitId: workingChild.commitId,
      versionKey: `${workingChild.repositoryId}:${workingChild.commitId}`,
      nodeType: workingChild.nodeType,
      localTransform: [...workingChild.transform],
    }
  })
  const root: ModelAssetNode = {
    ...input.currentResult.root,
    assemblyManifest: undefined,
    packageFiles: [],
    children: nextChildren,
  }
  const assemblyDocumentOverridesByNodeKey = new Map(input.currentResult.assemblyDocumentsByNodeKey)

  assemblyDocumentOverridesByNodeKey.set(rootNodeKey, input.workingAssemblyDocument)

  return executeProjectModelLoad({
    root,
    reader: input.reader,
    signal: input.signal,
    sceneBindings: [],
    assemblyDocumentOverridesByNodeKey,
    plan: createProjectModelLoadPlan({
      kind: 'root-load',
      root,
    }),
  })
}

/**
 * 创建空子节点时只写 working 装配文档，不把节点加入当前项目模型设计树。
 *
 * @param input 当前模型结果、工作草稿与后端新建节点。
 * @returns 新 working 文档和派生出的结构 key。
 */
export function createEmptyChildWorkingAssemblyDocument(
  input: CreateEmptyChildWorkingAssemblyDocumentInput,
): CreateEmptyChildWorkingAssemblyDocumentResult {
  const parentNodeKey = input.currentResult.root.nodeKey
  const keyDerivationRoot = createWorkingAwareRootForEmptyChildKey(input)
  const snapshot = createInsertedComponentNodeSnapshotResult({
    root: keyDerivationRoot,
    parentNodeKey,
    backendBaseUrl: input.backendBaseUrl,
    createdNode: input.createdNode,
    resolvedPlacementTransform: input.resolvedPlacementTransform,
  })
  const insertedTransform = toAssemblyTransform(
    input.resolvedPlacementTransform.matrix,
    snapshot.insertedNodeKey,
  )
  return {
    insertedNodeKey: snapshot.insertedNodeKey,
    workingAssemblyDocument: insertAssemblyChild(
      input.workingAssemblyDocument,
      {
        parentNodeKey,
        child: createAssemblyDocumentNodeFromCadNode({
          node: input.createdNode,
          nodeKey: snapshot.insertedNodeKey,
          transform: insertedTransform,
          fallbackDisplayName: input.fallbackDisplayName,
          adoptCreatedVersion: false,
        }),
      },
    ),
  }
}

/**
 * 插入零部件后只加载新增子树。
 *
 * @param input 当前模型结果、插入节点快照和读取器。
 * @returns 插入节点加载后的项目模型结果。
 */
export async function insertComponent(input: InsertComponentInput): Promise<InsertComponentResult> {
  return loadInsertedComponent({
    root: input.currentResult.root,
    parentNodeKey: input.parentNodeKey,
    backendBaseUrl: input.backendBaseUrl,
    createdNode: input.createdNode,
    resolvedPlacementTransform: input.resolvedPlacementTransform,
    reader: input.reader,
    sceneBindings: input.currentResult.sceneBindings,
    assemblyDocumentsByNodeKey: input.currentResult.assemblyDocumentsByNodeKey,
    signal: input.signal,
  })
}

/**
 * 只加载插入零部件对应的新增子树文件。
 *
 * 这里创建的 root 是本地加载快照，只用于让 cad-core 的 `insert-node` 计划定位新增子树；
 * 它不代表后端权威父装配树，也不负责刷新节点/分支列表。
 *
 * @param input 插入零部件加载上下文。
 * @returns 插入节点加载完成后的项目模型结果。
 */
export async function loadInsertedComponent(input: LoadInsertedComponentInput): Promise<LoadInsertedComponentResult> {
  const snapshot = createInsertedComponentNodeSnapshotResult({
    root: input.root,
    parentNodeKey: input.parentNodeKey,
    backendBaseUrl: input.backendBaseUrl,
    createdNode: input.createdNode,
    resolvedPlacementTransform: input.resolvedPlacementTransform,
  })
  const plan = createProjectModelLoadPlan({
    kind: 'insert-node',
    root: snapshot.root,
    insertedNodeKey: snapshot.insertedNodeKey,
    parentNodeKey: input.parentNodeKey,
  })

  const loadResult = await executeProjectModelLoad({
    root: snapshot.root,
    reader: input.reader,
    signal: input.signal,
    sceneBindings: input.sceneBindings,
    plan,
  })
  const assemblyDocumentsByNodeKey = new Map(input.assemblyDocumentsByNodeKey ?? [])

  // 局部插入只重新读取新增子树，旧装配文档仍是当前工作模型的一部分；新增读取结果覆盖同 key 旧值。
  loadResult.assemblyDocumentsByNodeKey.forEach((document, nodeKey) => {
    assemblyDocumentsByNodeKey.set(nodeKey, document)
  })

  return {
    ...loadResult,
    assemblyDocumentsByNodeKey,
    insertedNodeKey: snapshot.insertedNodeKey,
  }
}
